;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:tillamook-com":true});}catch(e){}}();import { useCallback, useId } from 'react';

import cx from 'classnames';
import PropTypes from 'prop-types';

import EntryLink from 'components/ui/Link/EntryLink';
import SvgIcon from 'components/ui/SvgIcon';

import { useSubNav } from './SubNavContext';
import { SubNavRight } from './SubNavRight';

import styles from './SubNavItems.module.scss';

/**
 * Renders a list of subnav items (group triggers + direct links).
 * Shared between desktop (hover) and mobile (click) navigation.
 */
const SubNavItems = ({ items, headingLink, headingTitle, onGroupClick }) => {
    const ctx = useSubNav();

    const instanceId = useId();

    const handleBlur = useCallback(
        e => {
            // Close right panel when focus leaves both the trigger and panel.
            if (!e.currentTarget.contains(e.relatedTarget)) {
                ctx?.setActiveSubGroupIndex(-1);
            }
        },
        [ctx]
    );
    const handleKeyDown = useCallback(
        e => {
            if (e.key === 'Escape') {
                ctx?.setActiveSubGroupIndex(-1);
                // Return focus to the group trigger.
                e.currentTarget.querySelector('button')?.focus();
            }
        },
        [ctx]
    );

    const handleGroupClick = (subItem, i) => () => {
        onGroupClick?.(subItem, i);
        ctx?.setActiveSubGroupIndex(i);
    };
    const handleGroupLinkClick = (subItem, i) => e => {
        // On mobile (onGroupClick provided), prevent navigation and open panel
        if (onGroupClick) {
            e.preventDefault();
            handleGroupClick(subItem, i)();
        }
    };
    const handleGroupMouseEnter = i => () => {
        ctx?.setActiveSubGroupIndex(i);
    };
    const handleGroupMouseLeave = () => {
        ctx?.setActiveSubGroupIndex(-1);
    };

    const renderNewBadge = item => {
        return item?.new && <span className={styles.newBadge}>NEW!</span>;
    };

    return (
        <>
            {headingLink && headingTitle && (
                <EntryLink
                    entry={headingLink}
                    className={styles.heading}
                    onMouseEnter={handleGroupMouseLeave}
                >
                    {headingTitle}
                    <div className={styles.circleArrowSolid}>
                        <SvgIcon type="chevronRight" />
                    </div>
                </EntryLink>
            )}
            {items.map((subItem, i) => {
                switch (subItem.__typename) {
                    case 'GlobalSiteNavSubNavGroup': {
                        const panelId = `${instanceId}-panel-${i}`;
                        const isGroupActive = ctx?.activeSubGroupIndex === i;
                        return (
                            <div
                                key={`group-${i}`}
                                style={{ width: '100%' }}
                                onBlur={handleBlur}
                                onKeyDown={handleKeyDown}
                            >
                                <EntryLink
                                    entry={subItem.link}
                                    className={cx(styles.groupTrigger, {
                                        [styles.groupTriggerActive]:
                                            isGroupActive,
                                    })}
                                    onClick={handleGroupLinkClick(subItem, i)}
                                    onMouseEnter={handleGroupMouseEnter(i)}
                                >
                                    {subItem.title}
                                    {renderNewBadge(subItem)}
                                    <div className={styles.circleArrowPlain}>
                                        <SvgIcon type="chevronRight" />
                                    </div>
                                </EntryLink>
                                <SubNavRight
                                    id={panelId}
                                    item={subItem}
                                    groupIndex={i}
                                />
                            </div>
                        );
                    }
                    case 'GlobalSiteNavSubNavItem':
                        return (
                            <EntryLink
                                key={`sub-${i}`}
                                entry={subItem.link}
                                className={styles.link}
                                onMouseEnter={handleGroupMouseLeave}
                            >
                                {subItem.title}
                                {renderNewBadge(subItem)}
                            </EntryLink>
                        );
                    default:
                        return null;
                }
            })}
        </>
    );
};

SubNavItems.propTypes = {
    items: PropTypes.arrayOf(PropTypes.object).isRequired,
    headingLink: PropTypes.object.isRequired,
    headingTitle: PropTypes.string.isRequired,
    onGroupClick: PropTypes.func,
};

/**
 * Renders fly-out menu items.
 * Shows an "All {title}" link followed by item links.
 * Shared between desktop right-column panels and mobile sub-sub panels.
 */
const SubNavGroupItems = ({ group }) => (
    <>
        {group.link && (
            <EntryLink entry={group.link} className={styles.link}>
                {`All ${group.title}`}
            </EntryLink>
        )}
        {group.itemsCollection.items.filter(Boolean).map((nestedItem, i) => (
            <EntryLink
                key={`nested-${i}`}
                entry={nestedItem.link}
                className={styles.link}
            >
                {nestedItem.title}
                {nestedItem.new && (
                    <span className={styles.newBadge}>NEW!</span>
                )}
            </EntryLink>
        ))}
    </>
);

SubNavGroupItems.propTypes = {
    group: PropTypes.shape({
        link: PropTypes.object,
        title: PropTypes.string.isRequired,
        itemsCollection: PropTypes.shape({
            items: PropTypes.arrayOf(PropTypes.object).isRequired,
        }).isRequired,
    }).isRequired,
};

export { SubNavItems, SubNavGroupItems };
